import { type FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getClient } from '../../api/clients'
import { createPatient, getPatient, updatePatient } from '../../api/patients'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass, textareaClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import type { PatientCreate, Sex, Species } from '../../types/patients'
import { SEX_LABELS, SPECIES_LABELS } from '../../types/patients'

interface FieldErrors {
  name?: string; species?: string; sex?: string
  date_of_birth?: string; weight_kg?: string; microchip_number?: string; notes?: string
}

type FormState = {
  name: string; species: string; sex: string; breed: string
  date_of_birth: string; is_desexed: string; weight_kg: string
  color: string; microchip_number: string; notes: string; allergies: string
}

const EMPTY: FormState = {
  name: '', species: '', sex: '', breed: '',
  date_of_birth: '', is_desexed: '', weight_kg: '',
  color: '', microchip_number: '', notes: '', allergies: '',
}

export default function PatientForm() {
  const { id: clientId, pid } = useParams<{ id: string; pid: string }>()
  const isEdit = Boolean(pid)
  const navigate = useNavigate()
  const toast = useToast()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [isDirty, setIsDirty] = useState(false)
  const [clientName, setClientName] = useState<string>('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useUnsavedChanges(isDirty && !submitting)

  useEffect(() => {
    if (!clientId) return
    const load = async () => {
      try {
        const client = await getClient(Number(clientId))
        setClientName(`${client.first_name} ${client.last_name}`)
        if (isEdit && pid) {
          const patient = await getPatient(Number(pid))
          setForm({
            name: patient.name, species: patient.species, sex: patient.sex,
            breed: patient.breed ?? '', date_of_birth: patient.date_of_birth ?? '',
            is_desexed: patient.is_desexed === true ? 'true' : patient.is_desexed === false ? 'false' : '',
            weight_kg: patient.weight_kg != null ? String(patient.weight_kg) : '',
            color: patient.color ?? '', microchip_number: patient.microchip_number ?? '',
            notes: patient.notes ?? '', allergies: patient.allergies ?? '',
          })
        }
      } catch {
        setLoadError('Could not load patient data.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [clientId, pid, isEdit])

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [field]: e.target.value }))
    setIsDirty(true)
  }

  const validateField = (field: keyof FieldErrors, value: string): string | undefined => {
    switch (field) {
      case 'name':            return value.trim().length === 0 ? 'Name is required' : value.trim().length > 100 ? 'Max 100 characters' : undefined
      case 'species':         return value === '' ? 'Species is required' : undefined
      case 'sex':             return value === '' ? 'Sex is required' : undefined
      case 'date_of_birth': {
        if (!value) return undefined
        const d = new Date(value)
        if (isNaN(d.getTime())) return 'Invalid date'
        if (d > new Date()) return 'Date of birth cannot be in the future'
        return undefined
      }
      case 'weight_kg': {
        if (!value) return undefined
        const n = parseFloat(value)
        if (isNaN(n)) return 'Enter a valid number'
        if (n <= 0) return 'Weight must be greater than 0'
        if (n > 1000) return 'Weight cannot exceed 1000 kg'
        return undefined
      }
      case 'microchip_number': return value.length > 50 ? 'Max 50 characters' : undefined
      case 'notes':           return value.length > 2000 ? 'Max 2000 characters' : undefined
      default: return undefined
    }
  }

  const handleBlur = (field: keyof FieldErrors, value: string) => {
    const err = validateField(field, value)
    setFieldErrors((prev) => { const next = { ...prev }; if (err) next[field] = err; else delete next[field]; return next })
  }

  const validateAll = (): boolean => {
    const fields: (keyof FieldErrors)[] = ['name', 'species', 'sex', 'date_of_birth', 'weight_kg', 'microchip_number', 'notes']
    const errors: FieldErrors = {}
    for (const field of fields) { const err = validateField(field, form[field as keyof FormState]); if (err) errors[field] = err }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setApiError(null)
    if (!validateAll()) return
    setSubmitting(true)
    const shared = {
      name: form.name.trim(), species: form.species as Species, sex: form.sex as Sex,
      breed: form.breed.trim() || undefined, date_of_birth: form.date_of_birth || undefined,
      is_desexed: form.is_desexed === 'true' ? true : form.is_desexed === 'false' ? false : null,
      weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
      color: form.color.trim() || undefined, microchip_number: form.microchip_number.trim() || undefined,
      notes: form.notes.trim() || undefined, allergies: form.allergies.trim() || undefined,
    }
    try {
      if (isEdit && pid) {
        await updatePatient(Number(pid), shared)
        setIsDirty(false)
        toast.success('Patient updated successfully.')
      } else {
        const payload: PatientCreate = { owner_client_id: Number(clientId), ...shared }
        await createPatient(payload)
        setIsDirty(false)
        toast.success('Patient added successfully.')
      }
      void navigate(`/clients/${clientId}`)
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 py-8">Loading…</div>

  if (loadError) {
    return (
      <ErrorBox>{loadError} <Link to={clientId ? `/clients/${clientId}` : '/clients'} className="text-accent no-underline font-medium">Back to client</Link></ErrorBox>
    )
  }

  return (
    <div>
      <nav className="text-[0.85rem] text-text-3 mb-5 flex items-center gap-[0.4rem]">
        <Link to="/clients" className="text-accent no-underline font-medium">Clients</Link>
        <span>›</span>
        {clientId && <Link to={`/clients/${clientId}`} className="text-accent no-underline font-medium">{clientName}</Link>}
        {clientId && <span>›</span>}
        <span>{isEdit ? 'Edit Patient' : 'New Patient'}</span>
      </nav>

      <h1 className="text-[1.4rem] font-bold text-text-heading mt-0 mb-7">
        {isEdit ? 'Edit Patient' : 'New Patient'}
      </h1>

      <Card className="max-w-[680px]">
        {apiError && <ErrorBox className="mb-5">{apiError}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          {/* Identity */}
          <div className="mb-8">
            <SectionTitle>Identity</SectionTitle>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="Name" htmlFor="p_name" required error={fieldErrors.name}>
                <input id="p_name" className={inputClass(!!fieldErrors.name)} type="text" value={form.name}
                  onChange={set('name')} onBlur={(e) => handleBlur('name', e.target.value)}
                  disabled={submitting} maxLength={100} autoFocus />
              </FormField>
              <FormField label="Species" htmlFor="p_species" required error={fieldErrors.species}>
                <select id="p_species" className={selectClass(!!fieldErrors.species)} value={form.species}
                  onChange={set('species')} onBlur={(e) => handleBlur('species', e.target.value)} disabled={submitting}>
                  <option value="">Select species…</option>
                  {(Object.keys(SPECIES_LABELS) as Species[]).map((k) => (
                    <option key={k} value={k}>{SPECIES_LABELS[k]}</option>
                  ))}
                </select>
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="Breed" htmlFor="p_breed">
                <input id="p_breed" className={inputClass()} type="text" value={form.breed}
                  onChange={set('breed')} disabled={submitting} maxLength={100} placeholder="e.g. Labrador Retriever" />
              </FormField>
              <FormField label="Colour" htmlFor="p_color">
                <input id="p_color" className={inputClass()} type="text" value={form.color}
                  onChange={set('color')} disabled={submitting} maxLength={100} placeholder="e.g. Golden" />
              </FormField>
            </div>
          </div>

          {/* Biological Details */}
          <div className="mb-8">
            <SectionTitle>Biological Details</SectionTitle>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="Sex" htmlFor="p_sex" required error={fieldErrors.sex}>
                <select id="p_sex" className={selectClass(!!fieldErrors.sex)} value={form.sex}
                  onChange={set('sex')} onBlur={(e) => handleBlur('sex', e.target.value)} disabled={submitting}>
                  <option value="">Select sex…</option>
                  {(Object.keys(SEX_LABELS) as Sex[]).map((k) => (
                    <option key={k} value={k}>{SEX_LABELS[k]}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Desexed" htmlFor="p_desexed">
                <select id="p_desexed" className={selectClass()} value={form.is_desexed}
                  onChange={set('is_desexed')} disabled={submitting}>
                  <option value="">Unknown</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="Date of birth" htmlFor="p_dob" error={fieldErrors.date_of_birth}>
                <input id="p_dob" className={inputClass(!!fieldErrors.date_of_birth)} type="date" value={form.date_of_birth}
                  onChange={set('date_of_birth')} onBlur={(e) => handleBlur('date_of_birth', e.target.value)}
                  disabled={submitting} max={new Date().toISOString().split('T')[0]} />
              </FormField>
              <FormField label="Weight (kg)" htmlFor="p_weight" error={fieldErrors.weight_kg}>
                <input id="p_weight" className={inputClass(!!fieldErrors.weight_kg)} type="number"
                  min="0.01" max="1000" step="0.01" value={form.weight_kg}
                  onChange={set('weight_kg')} onBlur={(e) => handleBlur('weight_kg', e.target.value)}
                  disabled={submitting} placeholder="e.g. 28.5" />
              </FormField>
            </div>
          </div>

          {/* Identification */}
          <div className="mb-8">
            <SectionTitle>Identification</SectionTitle>
            <FormField label="Microchip number" htmlFor="p_microchip" error={fieldErrors.microchip_number} className="max-w-[50%]">
              <input id="p_microchip" className={inputClass(!!fieldErrors.microchip_number)} type="text"
                value={form.microchip_number} onChange={set('microchip_number')}
                onBlur={(e) => handleBlur('microchip_number', e.target.value)}
                disabled={submitting} maxLength={50} placeholder="e.g. 982000123456789" />
            </FormField>
          </div>

          {/* Medical */}
          <div className="mb-8">
            <SectionTitle>Medical</SectionTitle>
            <FormField label="Allergies" htmlFor="p_allergies">
              <textarea id="p_allergies" className={textareaClass()} value={form.allergies}
                onChange={set('allergies')} disabled={submitting} maxLength={2000}
                placeholder="Known allergies or adverse reactions…" />
            </FormField>
            <FormField label="Notes" htmlFor="p_notes" error={fieldErrors.notes} hint={`${form.notes.length}/2000`}>
              <textarea id="p_notes" className={textareaClass(!!fieldErrors.notes)} value={form.notes}
                onChange={set('notes')} onBlur={(e) => handleBlur('notes', e.target.value)}
                disabled={submitting} maxLength={2000} placeholder="Medical history, behavioural notes…" />
            </FormField>
          </div>

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button type="submit" size="lg" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Patient'}
            </Button>
            <ButtonLink to={`/clients/${clientId}`} variant="outline" size="lg">Cancel</ButtonLink>
          </div>
        </form>
      </Card>
    </div>
  )
}
